import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../../config';
import {
  playQuizCorrectSound,
  playQuizWrongSound,
  playTimerTickSound,
} from '../../utils/soundEffects';

/**
 * QuizGame — Inkwell editorial battle gameplay.
 * Hairline timer bar at top, monospace counters, vermillion countdown,
 * 2x2 quiz grid with .quiz-answer states.
 */
function QuizGame({
  phase,
  gameType,
  questions,
  currentIndex,
  timeLimit,
  countdown,
  lastResult,
  totalCorrect,
  penaltyMs,
  playerProgress,
  playerId,
  isHost,
  hostKeys = {},
  hostAnswers = {},
  onSubmitAnswer,
  onAdvance,
}) {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showPenalty, setShowPenalty] = useState(false);
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const feedbackTimer = useRef(null);
  const penaltyTimer = useRef(null);
  const submittedRef = useRef(false);

  const currentQuestion = questions[currentIndex] || null;
  const isFinished = currentIndex >= questions.length;

  useEffect(() => {
    if (phase !== 'playing') return;
    setTimeLeft(timeLimit);
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;
        if (next <= 5 && next > 0) playTimerTickSound();
        if (next <= 0) {
          clearInterval(interval);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, timeLimit]);

  useEffect(() => {
    setSelectedAnswer(null);
    setShowFeedback(false);
    submittedRef.current = false;
  }, [currentIndex]);

  useEffect(() => {
    if (!lastResult || !selectedAnswer) return;
    setShowFeedback(true);
    if (lastResult.correct) {
      playQuizCorrectSound();
    } else {
      playQuizWrongSound();
      setShowPenalty(true);
      penaltyTimer.current = setTimeout(() => setShowPenalty(false), 1200);
    }
    feedbackTimer.current = setTimeout(() => {
      setShowFeedback(false);
      onAdvance();
    }, 400);
    return () => {
      clearTimeout(feedbackTimer.current);
      clearTimeout(penaltyTimer.current);
    };
  }, [lastResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswer = useCallback(
    (answerId) => {
      if (submittedRef.current || selectedAnswer || !currentQuestion) return;
      submittedRef.current = true;
      setSelectedAnswer(answerId);
      onSubmitAnswer(currentQuestion.questionId, answerId);
    },
    [selectedAnswer, currentQuestion, onSubmitAnswer]
  );

  useEffect(() => {
    // Hosts are observers — keyboard shortcuts for answering don't apply.
    if (isHost || phase !== 'playing' || isFinished) return;
    const handleKey = (e) => {
      const num = parseInt(e.key);
      if (num >= 1 && num <= 4 && currentQuestion?.options) {
        const option = currentQuestion.options[num - 1];
        if (option) handleAnswer(option.id);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isHost, phase, isFinished, currentQuestion, handleAnswer]);

  const otherPlayers = Object.entries(playerProgress)
    .filter(([id]) => id !== playerId)
    .map(([id, p]) => ({ id, ...p }));

  // The host's `playerProgress` may not include themselves, so for them
  // "all players" == "every entry in playerProgress" (i.e. all students).
  const allStudents = isHost
    ? Object.entries(playerProgress).map(([id, p]) => ({ id, ...p }))
    : otherPlayers;

  // ── Host (teacher) observer view — they don't answer questions; instead
  //    they watch students' live progress. Replaces both the countdown and
  //    the active-question screens for hosts only.
  if (isHost) {
    if (phase === 'countdown') {
      return (
        <div
          style={{
            minHeight: '100vh',
            background: 'var(--vermillion)',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div className="t-eyebrow" style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 24 }}>
            Starting in
          </div>
          <div
            style={{
              fontFamily: 'var(--font-han)',
              fontSize: 320,
              lineHeight: 1,
              fontWeight: 500,
              textShadow: '0 8px 40px rgba(0,0,0,0.2)',
            }}
          >
            {countdown}
          </div>
          <div style={{ marginTop: 32, fontSize: 18, opacity: 0.9 }}>
            {questions.length} questions · {timeLimit}s total
          </div>
        </div>
      );
    }

    return (
      <HostObserverView
        questions={questions}
        allStudents={allStudents}
        hostKeys={hostKeys}
        hostAnswers={hostAnswers}
        gameType={gameType}
      />
    );
  }

  // ── Countdown screen — vermillion bg, big hanzi numeral
  if (phase === 'countdown') {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--vermillion)',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="t-eyebrow" style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 24 }}>
          Get ready
        </div>
        <div
          style={{
            fontFamily: 'var(--font-han)',
            fontSize: 320,
            lineHeight: 1,
            fontWeight: 500,
            textShadow: '0 8px 40px rgba(0,0,0,0.2)',
          }}
        >
          {countdown}
        </div>
        <div style={{ marginTop: 32, fontSize: 18, opacity: 0.9 }}>
          {questions.length} questions · {timeLimit}s total
        </div>
      </div>
    );
  }

  // ── Done waiting screen
  if (isFinished) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--paper)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 40,
        }}
      >
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: 999,
            background: '#e0efe7',
            border: '2px solid var(--jade)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--jade)',
            marginBottom: 24,
          }}
        >
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="t-eyebrow" style={{ marginBottom: 12 }}>Submitted · 完成</div>
        <h1 className="t-display" style={{ marginBottom: 12 }}>You're done.</h1>
        <p style={{ fontSize: 22, marginBottom: 40 }}>
          <strong>{totalCorrect}</strong>{' '}
          <span className="t-small">/ {questions.length} correct</span>
        </p>

        {otherPlayers.length > 0 && (
          <div className="card" style={{ width: 480, padding: 24 }}>
            <div className="t-eyebrow" style={{ marginBottom: 16 }}>
              {otherPlayers.some((p) => !p.finished)
                ? `Waiting for ${otherPlayers.filter((p) => !p.finished).length} other${otherPlayers.filter((p) => !p.finished).length === 1 ? '' : 's'} to finish`
                : 'All players finished'}
            </div>
            {otherPlayers.map((p) => (
              <div key={p.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{p.name}</span>
                  <span
                    className="t-mono"
                    style={{
                      fontSize: 12,
                      color: p.finished ? 'var(--jade)' : 'var(--ink-3)',
                    }}
                  >
                    {p.finished ? '✓ done' : `${p.current}/${p.total}`}
                  </span>
                </div>
                <div className="progress-track">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${(p.current / p.total) * 100}%`,
                      background: p.finished ? 'var(--jade)' : 'var(--ink)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Active question
  const timerPercent = timeLimit > 0 ? (timeLeft / timeLimit) * 100 : 0;
  const isUrgent = timeLeft <= 10;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--paper)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Timer bar */}
      <div className="progress-track" style={{ height: 4, borderRadius: 0 }}>
        <div
          className={`progress-fill ${isUrgent ? 'progress-fill--urgent' : 'progress-fill--accent'}`}
          style={{ width: `${timerPercent}%` }}
        />
      </div>

      {/* Header row */}
      <div
        style={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 32px',
        }}
      >
        <div className="t-mono" style={{ fontSize: 13, letterSpacing: '0.08em' }}>
          {String(currentIndex + 1).padStart(2, '0')} / {String(questions.length).padStart(2, '0')}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="badge badge--jade">
            ✓ {totalCorrect} correct
          </span>
          <span
            className="t-mono"
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: isUrgent ? 'var(--vermillion)' : 'var(--ink)',
            }}
          >
            {timeLeft}s
            {showPenalty && (
              <span style={{ color: 'var(--vermillion)', marginLeft: 8 }}>+3s</span>
            )}
          </span>
        </div>
      </div>

      {/* Question + answers */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 32px',
        }}
      >
        <div style={{ maxWidth: 720, width: '100%' }}>
          <div className="t-eyebrow" style={{ textAlign: 'center', marginBottom: 16 }}>
            What does this character mean?
          </div>

          {gameType === 'picture' && currentQuestion?.image ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 40,
              }}
            >
              <img
                src={
                  currentQuestion.image.startsWith('http')
                    ? currentQuestion.image
                    : `${API_URL}${currentQuestion.image}`
                }
                alt="What character is this?"
                style={{
                  maxWidth: 400,
                  maxHeight: 280,
                  borderRadius: 'var(--r3)',
                  border: '1px solid var(--rule)',
                }}
              />
            </div>
          ) : (
            <div
              style={{
                fontFamily: 'var(--font-han)',
                fontSize: 220,
                lineHeight: 1,
                textAlign: 'center',
                letterSpacing: '0.04em',
                marginBottom: 40,
              }}
            >
              {currentQuestion?.hanzi}
            </div>
          )}

          <div className="qb-options" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {currentQuestion?.options?.map((option, idx) => {
              let className = 'quiz-answer';
              if (showFeedback && lastResult) {
                if (option.id === lastResult.correctId) {
                  className += ' quiz-answer--correct';
                } else if (option.id === selectedAnswer && !lastResult.correct) {
                  className += ' quiz-answer--wrong';
                } else {
                  className += ' quiz-answer--faded';
                }
              } else if (selectedAnswer === option.id) {
                className += ' quiz-answer--selected';
              }
              return (
                <button
                  key={option.id}
                  className={className}
                  onClick={() => handleAnswer(option.id)}
                  disabled={!!selectedAnswer}
                >
                  <span className="quiz-key">{idx + 1}</span>
                  <span style={{ flex: 1 }}>
                    {gameType === 'picture' ? option.hanzi : option.definition}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Live progress bars */}
      {otherPlayers.length > 0 && (
        <div
          style={{
            borderTop: '1px solid var(--rule)',
            padding: '16px 32px',
            background: 'var(--card)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(otherPlayers.length, 4)}, 1fr)`,
              gap: 24,
            }}
          >
            {otherPlayers.map((p) => (
              <div key={p.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</span>
                  <span className="t-mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    {p.finished ? '✓' : `${p.current}/${p.total}`}
                  </span>
                </div>
                <div className="progress-track">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${(p.current / p.total) * 100}%`,
                      background: p.finished ? 'var(--jade)' : 'var(--ink)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * HostObserverView — teacher-only dashboard rendered during the playing phase.
 * Combines a question reference (with answer key + per-option pick counts) and
 * a per-student row showing progress bar + per-question answer history.
 *
 * The host never sees the question UI itself; this is what they look at instead.
 */
function HostObserverView({ questions, allStudents, hostKeys, hostAnswers, gameType }) {
  const [expandedQId, setExpandedQId] = useState(null);

  const studentsFinished = allStudents.filter((s) => s.finished).length;
  const studentsTotal = allStudents.length;
  const savedRoomName = (typeof window !== 'undefined' && window.localStorage)
    ? localStorage.getItem('flashcards_room_name') || ''
    : '';

  const sortedStudents = allStudents
    .slice()
    .sort((a, b) => (b.correct ?? 0) - (a.correct ?? 0));

  // For each question, count how many students picked each option. Used to
  // render the expanded panel with pick distributions.
  const expandedQ = expandedQId
    ? questions.find((q) => q.questionId === expandedQId)
    : null;

  const expandedPicks = expandedQ
    ? expandedQ.options.map((opt) => {
        const pickedBy = sortedStudents
          .filter((s) => hostAnswers[s.id]?.byQuestion?.[expandedQ.questionId]?.answerId === opt.id)
          .map((s) => s.name);
        return { ...opt, pickedBy, isCorrect: opt.id === hostKeys[expandedQ.questionId] };
      })
    : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--paper)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 32px',
          borderBottom: '1px solid var(--rule)',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <div className="t-eyebrow">Observing · 老师视角</div>
          <h2 className="t-h3" style={{ marginTop: 4 }}>
            {savedRoomName ? `${savedRoomName} · ` : ''}
            {studentsFinished} / {studentsTotal} {studentsTotal === 1 ? 'student' : 'students'} finished
          </h2>
        </div>
        <span className="badge badge--mono">{questions.length} questions</span>
      </header>

      <div
        style={{
          flex: 1,
          padding: '24px clamp(16px, 4vw, 32px)',
          maxWidth: 1100,
          width: '100%',
          margin: '0 auto',
        }}
      >
        {/* Question reference strip — click any to inspect picks. */}
        <section style={{ marginBottom: 28 }}>
          <div className="t-eyebrow" style={{ marginBottom: 10 }}>
            Questions · 题目 ({questions.length})
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 8,
            }}
          >
            {questions.map((q, i) => {
              const correctOption = q.options.find((o) => o.id === hostKeys[q.questionId]);
              const isOpen = expandedQId === q.questionId;
              const answeredCount = sortedStudents.filter(
                (s) => hostAnswers[s.id]?.byQuestion?.[q.questionId]
              ).length;
              return (
                <button
                  key={q.questionId}
                  onClick={() => setExpandedQId(isOpen ? null : q.questionId)}
                  style={{
                    background: isOpen ? 'var(--ink)' : 'var(--card)',
                    color: isOpen ? 'var(--paper)' : 'var(--ink)',
                    border: '1px solid var(--rule)',
                    borderRadius: 8,
                    padding: '10px 8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <span
                    className="t-mono"
                    style={{ fontSize: 10, letterSpacing: '0.12em', opacity: 0.7 }}
                  >
                    Q{String(i + 1).padStart(2, '0')} · {answeredCount}/{studentsTotal}
                  </span>
                  <span
                    style={{
                      fontFamily: gameType === 'picture' ? 'var(--font-sans)' : 'var(--font-han)',
                      fontSize: gameType === 'picture' ? 13 : 24,
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {gameType === 'picture' ? '[image]' : (q.hanzi || '·')}
                  </span>
                  <span
                    className="t-small"
                    style={{
                      fontSize: 11,
                      color: isOpen ? 'rgba(255,255,255,0.7)' : 'var(--ink-3)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    ✓ {correctOption?.definition || correctOption?.hanzi || '—'}
                  </span>
                </button>
              );
            })}
          </div>

          {expandedQ && expandedPicks && (
            <div
              className="card"
              style={{ padding: 20, marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 4 }}>
                    Question detail
                  </div>
                  <div style={{ fontFamily: 'var(--font-han)', fontSize: 56, lineHeight: 1 }}>
                    {expandedQ.hanzi}
                  </div>
                  {expandedQ.pinyin && (
                    <div className="pinyin" style={{ fontSize: 14, marginTop: 4 }}>
                      {expandedQ.pinyin}
                    </div>
                  )}
                </div>
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => setExpandedQId(null)}
                  aria-label="Close question detail"
                >
                  ✕
                </button>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {expandedPicks.map((opt, idx) => (
                  <div
                    key={opt.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: '1px solid var(--rule)',
                      background: opt.isCorrect ? 'rgba(64,140,93,0.10)' : 'var(--paper-2)',
                    }}
                  >
                    <span
                      className="t-mono"
                      style={{
                        fontSize: 12,
                        width: 18,
                        color: opt.isCorrect ? 'var(--jade)' : 'var(--ink-3)',
                      }}
                    >
                      {opt.isCorrect ? '✓' : idx + 1}
                    </span>
                    <span style={{ flex: 1, fontWeight: opt.isCorrect ? 600 : 400 }}>
                      {gameType === 'picture' ? opt.hanzi : opt.definition}
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {opt.pickedBy.length === 0 ? (
                        <span className="t-small" style={{ color: 'var(--ink-3)' }}>—</span>
                      ) : (
                        opt.pickedBy.map((name) => (
                          <span
                            key={name}
                            className="t-small"
                            style={{
                              fontSize: 11,
                              padding: '2px 8px',
                              background: opt.isCorrect ? 'var(--jade)' : 'var(--vermillion)',
                              color: '#fff',
                              borderRadius: 999,
                              fontWeight: 500,
                            }}
                          >
                            {name}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Per-student rows: progress + answer strip. */}
        <section>
          <div className="t-eyebrow" style={{ marginBottom: 10 }}>
            Students · 学生
          </div>
          {studentsTotal === 0 ? (
            <p className="t-small" style={{ textAlign: 'center', padding: '32px 0' }}>
              Waiting for students to start answering…
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sortedStudents.map((s) => {
                const total = s.total || questions.length || 1;
                const pct = Math.min(100, ((s.current ?? 0) / total) * 100);
                const studentAnswers = hostAnswers[s.id]?.byQuestion || {};
                const currentQNum = s.finished ? total : (s.current ?? 0) + 1;
                return (
                  <div
                    key={s.id}
                    className="card"
                    style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontWeight: 600 }}>{s.name}</span>
                      <span
                        className="t-mono"
                        style={{
                          fontSize: 13,
                          color: s.finished ? 'var(--jade)' : 'var(--ink-3)',
                        }}
                      >
                        {s.finished
                          ? `✓ done · ${s.correct ?? 0}/${total}`
                          : `on Q${currentQNum} · ${s.correct ?? 0} correct`}
                      </span>
                    </div>
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${pct}%`,
                          background: s.finished ? 'var(--jade)' : 'var(--ink)',
                        }}
                      />
                    </div>
                    {/* Per-question answer dots */}
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 4,
                      }}
                    >
                      {questions.map((q, i) => {
                        const ans = studentAnswers[q.questionId];
                        const isCurrent = !ans && i === (s.current ?? 0) && !s.finished;
                        const isUpcoming = !ans && !isCurrent;
                        let bg = 'var(--paper-2)';
                        let color = 'var(--ink-3)';
                        let label = i + 1;
                        if (ans) {
                          if (ans.correct) {
                            bg = 'var(--jade)';
                            color = '#fff';
                            label = '✓';
                          } else {
                            bg = 'var(--vermillion)';
                            color = '#fff';
                            // Show which option they picked (1–4) on wrong answers.
                            const pickedIdx = q.options.findIndex((o) => o.id === ans.answerId);
                            label = pickedIdx >= 0 ? pickedIdx + 1 : '✗';
                          }
                        } else if (isCurrent) {
                          bg = 'var(--ink)';
                          color = 'var(--paper)';
                          label = '⋯';
                        } else if (isUpcoming) {
                          bg = 'var(--paper-2)';
                          color = 'var(--ink-3)';
                          label = '·';
                        }
                        return (
                          <button
                            key={q.questionId}
                            onClick={() => setExpandedQId(q.questionId)}
                            title={`Q${i + 1}`}
                            className="t-mono"
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 6,
                              background: bg,
                              color,
                              border: 'none',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default QuizGame;
